/**
 * 사진 올리기 화면 쪽 (기반 공사 5단계)
 *
 * - 아이폰 사진(HEIC)은 서버 부품이 읽지 못해 화면에서 JPEG로 바꿔 올린다. 휴대폰 사파리는 저절로 바꿔 보내고,
 *   컴퓨터로 옮긴 .heic 파일만 여기서 바꾼다. 바꾸는 부품(heic-to 1.6.5, 약 3MB)은 HEIC를 고를 때만 내려받는다.
 * - PDF(등록증, 통장 사본)는 그대로 보낸다.
 * - 휴대폰에서 카메라를 바로 열려면 입력칸에 capture="environment"를 단다(PhotoInput의 camera).
 */

export interface PickedFile {
  /** data:...;base64, 모양 */
  data_url: string;
  kind: '사진' | 'PDF';
  /** 아이폰 사진을 바꿨는지 */
  converted: boolean;
  name: string;
  bytes: number;
}

function readAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('파일을 읽지 못했어요.'));
    reader.readAsDataURL(blob);
  });
}

export function looksHeic(file: File): boolean {
  return /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

/** 고른 파일을 서버에 보낼 모양으로. 아이폰 사진은 JPEG로 바꾼다. */
export async function readPicked(file: File): Promise<PickedFile> {
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    return { data_url: await readAsDataUrl(file), kind: 'PDF', converted: false, name: file.name, bytes: file.size };
  }
  if (looksHeic(file)) {
    const { heicTo } = await import('heic-to');
    const jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.9 });
    return { data_url: await readAsDataUrl(jpeg), kind: '사진', converted: true, name: file.name, bytes: jpeg.size };
  }
  return { data_url: await readAsDataUrl(file), kind: '사진', converted: false, name: file.name, bytes: file.size };
}

/** 받는 파일 종류: 사진(아이폰 사진 포함)과 PDF */
export const ACCEPT_PHOTO_PDF = 'image/*,.heic,.heif,application/pdf';
